import {
  ArrowRight,
  Check,
  CheckCircle2,
  CircleHelp,
  Clock3,
  LockKeyhole,
  ShieldCheck,
  Sparkles,
  UserRound,
  UsersRound,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { SEO } from "@/lib/seo";
import "@/styles/onboarding-mockup.css";

const inviteTrust = [
  {
    icon: ShieldCheck,
    title: "Verified before publishing",
    copy: "Profile changes are reviewed before GPs rely on them.",
  },
  {
    icon: Clock3,
    title: "Built for limited time",
    copy: "The confirmation flow is short, clear and click-first.",
  },
  {
    icon: CircleHelp,
    title: "Not sure is accepted",
    copy: "Uncertain answers can be checked with the practice later.",
  },
  {
    icon: LockKeyhole,
    title: "No patient information",
    copy: "The onboarding flow only asks for public profile details.",
  },
];

const inviteSteps = [
  "Create or sign in to a PulseBoard specialist account.",
  "Claim or confirm the prepared specialist profile.",
  "Approve the GP-facing referral details.",
  "Keep the profile current with lightweight reminders.",
];

export default function SpecialistInvitation() {
  const { isLoading, isAuthenticated, roles, register, login, logout } = useAuth();
  const specialistRedirect = `${window.location.origin}/specialist/onboarding?invite=specialist`;
  const isSpecialist = roles.includes("specialist");

  const startRegistration = () => {
    void register({ redirectUri: specialistRedirect });
  };

  const signIn = () => {
    void login({ redirectUri: specialistRedirect });
  };

  return (
    <main className="onboarding-board onb-invite-page">
      <SEO
        title="Specialist invitation - PulseBoard"
        description="Join PulseBoard and confirm your specialist referral profile."
        path="/onboarding"
        noindex
      />

      <section className="onb-wizard-shell" aria-label="PulseBoard specialist invitation">
        <header className="onb-wizard-header">
          <div className="onb-brand-lockup">
            <img className="onb-logo" src="/pulseboard-favicon-256.png" alt="PulseBoard" />
            <div>
              <span className="onb-brand-kicker">Specialist invitation</span>
              <h1><span>PulseBoard</span> <span>Onboarding</span></h1>
              <p>Simple. Accurate. Always up to date.</p>
            </div>
          </div>

          <div className="onb-trust-strip" aria-label="PulseBoard trust signals">
            {inviteTrust.map((item) => (
              <article key={item.title} className="onb-trust-item">
                <item.icon size={31} strokeWidth={1.8} />
                <div>
                  <h2>{item.title}</h2>
                  <p>{item.copy}</p>
                </div>
              </article>
            ))}
          </div>
        </header>

        <section className="onb-invite-layout">
          <article className="onb-invite-hero">
            <span className="onb-secure-chip">
              <LockKeyhole size={16} /> Secure specialist sign-up link
            </span>
            <h2>Join PulseBoard and approve your referral profile in minutes.</h2>
            <p>
              PulseBoard helps GPs see accurate specialist availability, wait
              times and referral instructions. Your profile is not published
              from this invitation alone. You stay in control of what is approved.
            </p>

            <div className="onb-invite-actions">
              {isAuthenticated && isSpecialist ? (
                <Link className="onb-nav-button is-primary" to="/specialist/onboarding">
                  Continue onboarding <ArrowRight size={19} />
                </Link>
              ) : (
                <button
                  type="button"
                  className="onb-nav-button is-primary"
                  onClick={startRegistration}
                  disabled={isLoading}
                >
                  Create specialist account <ArrowRight size={19} />
                </button>
              )}

              {isAuthenticated && !isSpecialist ? (
                <button
                  type="button"
                  className="onb-nav-button"
                  onClick={() => void logout({ redirectUri: window.location.href })}
                >
                  Use a different account
                </button>
              ) : (
                <button
                  type="button"
                  className="onb-nav-button"
                  onClick={signIn}
                  disabled={isLoading}
                >
                  I already have an account
                </button>
              )}
            </div>

            <div className="onb-assurance-list">
              <span><Clock3 size={16} /> Usually 3 minutes</span>
              <span><CheckCircle2 size={16} /> Specialist approved</span>
              <span><LockKeyhole size={16} /> No patient data</span>
            </div>
          </article>

          <aside className="onb-invite-card">
            <div className="onb-invite-card-header">
              <span><Sparkles size={24} /></span>
              <div>
                <h2>What happens after sign-up?</h2>
                <p>One low-friction profile confirmation flow.</p>
              </div>
            </div>

            <ol className="onb-invite-step-list">
              {inviteSteps.map((step) => (
                <li key={step}>
                  <span><Check size={16} /></span>
                  <p>{step}</p>
                </li>
              ))}
            </ol>

            <div className="onb-invite-preview">
              <div className="onb-avatar">JS</div>
              <div>
                <strong>Specialist profile preview</strong>
                <p>Identity, availability, wait time and referral method.</p>
              </div>
            </div>
          </aside>
        </section>

        <section className="onb-invite-proof" aria-label="Why specialists are invited">
          <article>
            <UserRound size={24} />
            <h2>For specialists</h2>
            <p>Approve what GPs see without handling a long admin form.</p>
          </article>
          <article>
            <UsersRound size={24} />
            <h2>For practice teams</h2>
            <p>Reception or practice managers can help prepare the details first.</p>
          </article>
          <article>
            <ShieldCheck size={24} />
            <h2>For GPs</h2>
            <p>Current availability reduces unnecessary phone calls and misdirected referrals.</p>
          </article>
        </section>
      </section>
    </main>
  );
}
